import { Title, usePermissions } from 'react-admin';
import { Box, Typography, Grid, Skeleton, Card, CardContent, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import { useDashboardAccess } from '../hooks/useDashboardAccess';
import { DASHBOARD_SECTIONS, type SectionConfig } from './Dashboard/sections';

const SUPPORT_EMAIL = 'c360Support@gmail.com';
const ACCENT = '#b54400';
const PRIMARY = '#31006F';

const HeroBanner = ({ subtitle }: { subtitle: string }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      mb: 4,
      p: { xs: 3, md: 4.5 },
      borderRadius: 3,
      background: `linear-gradient(135deg, ${PRIMARY} 0%, #5a0099 100%)`,
      color: '#fff',
      boxShadow: '0 8px 28px rgba(49,0,111,0.25)',
    }}
  >
    {/* soft decorative glow */}
    <Box
      sx={{
        position: 'absolute',
        top: -80,
        right: -60,
        width: 260,
        height: 260,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(181,68,0,0.35) 0%, rgba(181,68,0,0) 70%)',
        pointerEvents: 'none',
      }}
    />
    <Typography variant="overline" sx={{ letterSpacing: '0.18em', opacity: 0.8, fontWeight: 700 }}>
      C360 Admin Portal
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
      Welcome back
    </Typography>
    <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 720, lineHeight: 1.7 }}>
      {subtitle}
    </Typography>
  </Box>
);

const NoRolesMessage = () => (
  <Box
    sx={{
      p: 4,
      borderRadius: 3,
      background: 'linear-gradient(135deg, rgba(49,0,111,0.04) 0%, rgba(181,68,0,0.04) 100%)',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
      No roles assigned
    </Typography>
    <Typography variant="body1" color="text.secondary" component="div">
      You do not have any roles assigned yet. Please contact{' '}
      <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: 'primary.main', fontWeight: 600 }}>
        {SUPPORT_EMAIL}
      </Box>{' '}
      to be assigned roles and access the admin portal.
    </Typography>
  </Box>
);

const SectionCard = ({ section, links }: { section: SectionConfig; links: typeof section.links }) => {
  const navigate = useNavigate();
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`,
          opacity: 0,
          transition: 'opacity 0.2s ease',
        },
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: 'transparent',
          boxShadow: '0 12px 30px rgba(49,0,111,0.12)',
        },
        '&:hover::before': { opacity: 1 },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: ACCENT,
              backgroundColor: 'rgba(181,68,0,0.10)',
            }}
          >
            {section.icon}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
              {section.title}
            </Typography>
          </Box>
          <Chip
            label={links.length}
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              fontWeight: 700,
              fontSize: '0.72rem',
              color: PRIMARY,
              backgroundColor: 'rgba(49,0,111,0.08)',
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          {section.description}
        </Typography>
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {links.map(({ label, to }) => (
            <Box
              component="li"
              key={to}
              onClick={() => navigate(to)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 1.5,
                mx: -1.5,
                borderRadius: 1.5,
                cursor: 'pointer',
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '0.9rem',
                transition: 'background-color 0.15s ease, color 0.15s ease',
                '& .chevron': { transition: 'transform 0.15s ease', opacity: 0.5 },
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'primary.main',
                },
                '&:hover .chevron': { transform: 'translateX(3px)', opacity: 1 },
              }}
            >
              {label}
              <ChevronRightIcon className="chevron" sx={{ fontSize: 20 }} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const QuickLinksHeading = () => (
  <Typography
    variant="overline"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      color: 'text.secondary',
      fontWeight: 700,
      letterSpacing: '0.1em',
      '&::after': { content: '""', display: 'block', width: 40, height: 2, borderRadius: 1, backgroundColor: ACCENT },
    }}
  >
    Quick Links
  </Typography>
);

const INTRO_TEXT = 'This portal gives your team centralised access to manage client users, configure payments, oversee insurance applications, and administer platform settings.';

export const Dashboard = () => {
  const { permissions, isPending } = usePermissions();
  const { accessMap, isAccessPending } = useDashboardAccess();

  const hasNoRoles = !isPending && (!permissions || permissions.length === 0);
  const isLoading = isPending || isAccessPending;

  const filteredSections = useMemo(() => DASHBOARD_SECTIONS.map((s) => ({ ...s, links: s.links.filter((l) => accessMap[l.resource]) })).filter((s) => s.links.length > 0), [accessMap]);

  if (isLoading) {
    return (
      <>
        <Title title="Home" />
        <Box sx={{ py: 4, px: 3 }}>
          <Box sx={{ mb: 4, p: 4, borderRadius: 3, background: `linear-gradient(135deg, ${PRIMARY} 0%, #5a0099 100%)`, color: '#fff' }}>
            <Skeleton variant="text" width="60%" height={48} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
            <Skeleton variant="text" width="80%" height={24} sx={{ bgcolor: 'rgba(255,255,255,0.2)', mt: 1 }} />
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </>
    );
  }

  if (hasNoRoles) {
    return (
      <>
        <Title title="Home" />
        <Box sx={{ py: 4, px: 3 }}>
          <HeroBanner subtitle={INTRO_TEXT} />
          <NoRolesMessage />
        </Box>
      </>
    );
  }

  return (
    <>
      <Title title="Home" />
      <Box sx={{ py: 4, px: 3 }}>
        <HeroBanner subtitle={INTRO_TEXT} />

        {filteredSections.length > 0 ? (
          <>
            <QuickLinksHeading />
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {filteredSections.map((section) => (
                <Grid key={section.title} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <SectionCard section={section} links={section.links} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <>
            <QuickLinksHeading />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.7 }}>
              The C360 Admin Portal provides centralised access to manage client users, configure payments, oversee insurance applications, and administer platform settings. Use the sidebar to navigate.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} component="div">
              You have roles but no section access yet. Contact{' '}
              <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: 'primary.main', fontWeight: 600 }}>
                {SUPPORT_EMAIL}
              </Box>{' '}
              if you need access.
            </Typography>
          </>
        )}
      </Box>
    </>
  );
};
